'use client'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import _ from "lodash"
import { useWindowSize } from 'react-use'
import { ProjectTile } from '.'
import { PrismicRichText } from '@prismicio/react'


interface MarketingRoadMapCalenderProps {
    data: any | null;
}

const MarketingRoadMap: React.FC<MarketingRoadMapCalenderProps> = ({ data }) => {

    const toDate = (value: string) => new Date(value);
    const diffDays = (a: Date, b: Date) => (a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24);
    const formatMMMYY = (date: Date) =>
        new Intl.DateTimeFormat('en-NZ', { month: 'short', year: '2-digit' }).format(date).replace(',', '');

    // Get start and end dates 
    const rowsRef = useRef(null) as any
    const monthsRef = useRef(null) as any
    const gridRef = useRef(null) as any
    const projRef = useRef(null) as any

    const [ rowWidth, setRowWidth ] = useState(null) as any

    const { width } = useWindowSize();

    const dateRanges =[] as any

    data?.map((data: any)=>{
        data.data?.slices[0].primary.items?.map((item: any)=>{
            // console.log({item: data.uid, start: item.date_start, end: item.date_end})
        dateRanges.push({start: item.date_start, end: item.date_end})
    })})

    const earliestStartDate = new Date(Math.min(...dateRanges.map((range: any) => toDate(range.start).getTime())));
    const latestEndDate = new Date(Math.max(...dateRanges.map((range: any) => toDate(range.end).getTime())));
    const daysDifference = Math.ceil(diffDays(latestEndDate, earliestStartDate));


    const monthsList = useMemo(() => {
        const list: string[] = [];
        const cursor = new Date(earliestStartDate.getFullYear(), earliestStartDate.getMonth(), 1);
        const end = new Date(latestEndDate.getFullYear(), latestEndDate.getMonth(), 1);
        while (cursor <= end) {
            list.push(formatMMMYY(cursor));
            cursor.setMonth(cursor.getMonth() + 1);
        }
        return list;
    }, [earliestStartDate, latestEndDate]);

    const showDots = (id: any, dot: any) => {
        // console.log(dot, id)
        const selector = document.querySelectorAll('div.'+id);
    
        selector.forEach(element => { 
            element.classList.toggle("hide");
        });
        dot.children[0]?.classList.toggle("on");
    }

    const uniquePhasesAndColors = _(data)
        .flatMap(entry => entry.data?.slices || [])
        .filter(slice => slice.primary?.items?.length)
        .flatMap(slice => slice.primary.items)
        .filter(item => item?.project_phase)
        .map(item => ({
            project_phase: item.project_phase
        }))
        .uniqBy(item => item.project_phase)
        .value();



    const groupedData = _.groupBy(data, (rmdata) => JSON.stringify(rmdata.data.category_description));

    const customOrder = [
    'Pending review',
    'Discovery',
    'Development',
    'Approvals',
    'RSP consultations',
    'In market',
    'Reporting & review',
    'On hold',
    ];
    
    uniquePhasesAndColors.sort((a: any, b: any) => customOrder.indexOf(a.project_phase) - customOrder.indexOf(b.project_phase));

    useEffect(() => {
        const monthsCount = monthsList.length-1;
        const visibleMonths = 12; // Number of months to show in the initial view pane
        const columnWidth = window.innerWidth < 800 ? 60  : rowsRef?.current?.offsetWidth / visibleMonths;
    
        // Calculate the total width for 12 months initially, and then expand for the full list
        const initialWidth = Math.round(columnWidth * visibleMonths);
        const totalWidth = Math.round(columnWidth * monthsCount);
    
        // Set the initial width for the viewport, and the total width for the full content
        setRowWidth(`${totalWidth-300}px`);
        monthsRef.current.style.width = `${totalWidth}px`;
        gridRef.current.style.width = `${totalWidth-300}px`;
        projRef.current.style.width = `${totalWidth-300}px`;
    
        // Set the viewport width to show exactly 12 months
        rowsRef.current.style.width = `${initialWidth}px`;
    }, [width, monthsList]);

    const phaseColor = (type: any) => {
        switch(type) {
            case 'pending-review':
                return "bg-[#ed5321]"
            break;
            case 'discovery':
                return "bg-[#e17e55]"
            break;
            case 'development':
                return "bg-[#ebad94]"
            break;
            case 'approvals':
                return "bg-[#dfcccb]"
            break;
            case 'rsp-consultations':
                return "bg-[#acacac]"
            break;
            case 'in-market':
                return "bg-[#3e68ff]"
            break;
            case 'reporting--review':
                return "bg-[#3e68ff]"
            break;
            case 'on-hold':
                return "bg-[#ed0900]"
            break;
            default:
        }
    }

    return (
        <div className="my-16">
            <div className="grid grid-cols-12 gap-0 font-fellix mt-16 px-4 md:px-8 lg:px-12">
                <div className="col-span-12 md:col-span-3">
                    <h3 className="text-[28px]">Calendar</h3>
                </div>
                <div className="col-span-12  md:col-span-9 flex md:justify-end items-center">
                    <ul className="m-0 p-0">
                        {uniquePhasesAndColors?.map((item, index)=>{
                            return (
                                <li className="inline-block text-[12px]" key={index} onClick={(e)=>showDots(item.project_phase.toLowerCase().replace(/ /g,"-").replace(/\&/g, ""), e.target)}><span className={`inline-block w-3 h-3 relative top-[2px] ml-3 mr-2 rounded-full ${phaseColor(item.project_phase.toLowerCase().replace(/ /g,"-").replace(/\&/g, ""))}`}></span> {item.project_phase}</li>
                            )
                        })}
                    </ul>
                </div>
            </div> 
            <div className="overflow-x-scroll overflow-y-scroll relative h-[80vh] pl-4 md:pl-8 lg:pl-12">
                <div className="flex justify-between sticky top-[0] pt-[30px] pr-0 pb-[10px] pl-[170px] z-20 bg-white" ref={monthsRef}>
                    {monthsList.map((item: any, index: number)=>{
                        return (<div className=" font-semibold text-xs !leading-[18px] !text-center block" key={index}>{item}</div>)
                    })}
                </div>
                <div className="flex justify-between pl-[170px] pt-[30px] absolute" ref={gridRef}>
                    {monthsList.map((item: any, index: number)=>{
                        return (<div className="flex-[1] [border-right:1px_solid_$jadtintgreen] h-full" key={index}></div>)
                    })}
                </div>
                <div className="mt-[20px] pb-[40px] relative" ref={projRef}> 
                    {Object.keys(groupedData).map((description: any, index: number) => (
                        <Fragment key={index}>
                            <div className="block !sticky left-[0] w-[160px] max-w-[160px] font-semibold text-[14px] leading-[14px] mt-[20px] mx-[0] mb-4  bg-white">
                                <PrismicRichText field={JSON.parse(description)} />
                            </div>
                            <div>
                                {groupedData[description].map((rmdata) =>
                                    rmdata.data.slices?.map((slice: any, bindex: number) => (
                                        <Fragment key={index + bindex}>
                                        <div className={
                                            "sticky left-0 font-normal block border-[1px] border-black border-b-none text-[14px] leading-[14px] p-[8px] pb-[10px] w-[150px] max-w-[150px] bg-white " +
                                            (bindex === 0 ? " border-t-[1px] border-t-black rounded-t-[5px] " : bindex === rmdata.data.slices?.length - 1 ? " border-b-none rounded-b-[5px] " : "")
                                            }>
                                            {slice.primary.project_name}
                                        </div>
                                        <div className="absolute left-[170px] border-[1px] border-jadetintgrey" style={{ width: rowWidth }}>
                                            {slice.primary.items?.map((item: any, kindex: number) => {
                                            if (item.project_phase) {
                                                return (
                                                <Fragment key={kindex + index + bindex}>
                                                    <ProjectTile
                                                    phaseColor={phaseColor}
                                                    data={item}
                                                    key={index}
                                                    open={index}
                                                    daysDifference={daysDifference}
                                                    earliestStartDate={earliestStartDate}
                                                    top={index + bindex}
                                                    rowWidth={rowWidth}
                                                    />
                                                </Fragment>
                                                );
                                            }
                                            })}
                                        </div>
                                        </Fragment>
                                    ))
                                )}
                            </div>
                        </Fragment>
                    ))}
                    <div ref={rowsRef}></div>
                </div>
            </div>
        </div>
    )

}

export default MarketingRoadMap;
